import { useContext, useEffect, useState, useMemo } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import {
    STR_LOANS,
    STR_TOTAL_LOANS,
    STR_NO_RECORD,
    STR_SEARCH_HERE,
} from "../Strings";
import { 
    MdSearch, MdPeople, MdPerson, MdCalendarToday, 
    MdAccountBalanceWallet, MdTrendingUp, MdSwipe 
} from 'react-icons/md';

export default function View(props) {
    const appContext = useContext(AppContext);

    /* States */
    const [ready, setReady] = useState(false);
    const [filter, setFilter] = useState("");
    const [activeTab, setActiveTab] = useState("all");
    const [showScrollPrompt, setShowScrollPrompt] = useState(false);

    async function init() {
        setReady(false);
        await Promise.all([appContext.getLoans()]);
        setReady(true);
        
        // Trigger life-like scroll prompt
        setTimeout(() => setShowScrollPrompt(true), 800);
        setTimeout(() => setShowScrollPrompt(false), 3800);
    }

    useEffect(() => {
        init();
    }, []);

    const filteredList = useMemo(() => {
        const list = appContext.loans || [];
        const filtered = list.filter(item => {
            const isMine = String(item.fullname).toLowerCase() === String(appContext.currentUserData?.fullname).toLowerCase();
            const matchesSearch = String(item.fullname).toLowerCase().includes(filter.toLowerCase());
            return activeTab === "mine" ? isMine : matchesSearch;
        });

        return filtered.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    }, [appContext.loans, appContext.currentUserData, filter, activeTab]);

    const grandTotal = useMemo(() => {
        return filteredList.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    }, [filteredList]);

    const renderStatus = (status) => {
        const s = String(status).toLowerCase();
        let styles = { bg: 'rgba(100, 116, 139, 0.1)', color: '#64748b', label: status || 'Unknown' };

        if (s === 'paid' || s === 'completed') {
            styles = { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: 'Paid' };
        } else if (s === 'pending' || s === 'active') {
            styles = { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'Pending' };
        } else if (s === 'overdue') {
            styles = { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: 'Overdue' };
        }

        return (
            <span className="badge rounded-pill" style={{ 
                backgroundColor: styles.bg, 
                color: styles.color, 
                padding: '6px 12px',
                fontWeight: '600',
                fontSize: '10px',
                letterSpacing: '0.3px'
            }}>
                {styles.label.toUpperCase()}
            </span>
        );
    };

    if (!ready) {
        return <div className="d-flex vh-100 align-items-center justify-content-center bg-white">{getInlineLoader()}</div>;
    }

    return (
        <div className="mNoScrollBar" style={{ 
            width: "100%", height: "100%", overflowY: "auto", 
            backgroundColor: "#f8fafc", paddingBottom: "100px",
            position: "relative"
        }}>
            <style>{`
                .table-container {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);
                    overflow: hidden;
                    position: relative;
                }
                .mobile-scroll-table {
                    display: block;
                    width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }

                /* Desktop specific: Ensure table spans full width */
                @media (min-width: 769px) {
                    .mobile-scroll-table {
                        display: table !important;
                    }
                }

                .mobile-scroll-table th {
                    background-color: #fcfcfd;
                    padding: 1rem 1.25rem !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                }
                .mobile-scroll-table td {
                    white-space: nowrap;
                    padding: 1.1rem 1.25rem !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                }
                .loan-row { transition: background 0.2s ease; }
                .loan-row:hover { background-color: #f8fafc; }
                
                @keyframes bounceHorizontal {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(10px); }
                }
                .scroll-prompt-badge {
                    background: #1e293b;
                    color: white;
                    padding: 10px 20px;
                    border-radius: 50px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
                    animation: bounceHorizontal 1.5s infinite;
                }

                @media (max-width: 768px) {
                    .sticky-col {
                        position: sticky;
                        left: 0;
                        background-color: white !important;
                        z-index: 1;
                        border-right: 2px solid #f1f5f9 !important;
                    }
                }
            `}</style>

            <div className="container py-4">
                
                {/* 1. Dashboard Summary Card */}
                <div className="card border-0 shadow-sm mb-4" style={{ 
                    borderRadius: '24px', 
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    color: 'white'
                }}>
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="text-primary font-bold mb-1">
                                    {STR_TOTAL_LOANS[appContext.language]}
                                </h5>
                                <h1 className="font-bold mb-0" style={{ fontSize: '2.2rem' }}>
                                    {appContext.currency} {grandTotal.toLocaleString()}
                                </h1>
                                <div className="mt-3 small bg-white bg-opacity-10 d-inline-flex px-3 py-1 rounded-pill align-items-center">
                                    <MdTrendingUp className="me-2 text-info" /> 
                                    <span className="opacity-75">{filteredList.length} Active Records</span>
                                </div>
                            </div>
                            <div className="bg-white bg-opacity-10 p-3 rounded-circle">
                                <MdAccountBalanceWallet size={32} className="text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Filters & Search */}
                <div className="row g-3 mb-4">
                    <div className="col-12 col-lg-5">
                        <div className="bg-white p-1 d-flex shadow-sm rounded-pill border">
                            <button 
                                className={`btn flex-fill border-0 rounded-pill py-2 ${activeTab === 'all' ? 'bg-dark text-white shadow' : 'text-muted'}`}
                                onClick={() => setActiveTab('all')}
                                style={{ fontSize: '14px', fontWeight: '600', transition: '0.3s' }}
                            >
                                <MdPeople className="me-1" /> All Loans
                            </button>
                            <button 
                                className={`btn flex-fill border-0 rounded-pill py-2 ${activeTab === 'mine' ? 'bg-dark text-white shadow' : 'text-muted'}`}
                                onClick={() => { setActiveTab('mine'); setFilter(""); }}
                                style={{ fontSize: '14px', fontWeight: '600', transition: '0.3s' }}
                            >
                                <MdPerson className="me-1" /> My Loans
                            </button>
                        </div>
                    </div>
                    
                    <div className="col-12 col-lg-7">
                        <div className="input-group bg-white shadow-sm rounded-pill px-3 border d-flex align-items-center">
                            <MdSearch size={22} className="text-muted me-2" />
                            <input
                                className="form-control border-0 bg-transparent ps-2 fw-medium"
                                onChange={(e) => setFilter(e.target.value)}
                                value={filter}
                                placeholder={STR_SEARCH_HERE[appContext.language]}
                                style={{ height: '45px', boxShadow: 'none', fontSize: '14px' }}
                            />
                        </div>
                    </div>
                </div>

                {/* 3. Table Container */}
                <div className="table-container border">
                    {showScrollPrompt && (
                        <div className="position-absolute w-100 h-100 d-flex d-md-none align-items-center justify-content-center" 
                             style={{ zIndex: 10, background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(2px)', transition: '0.5s' }}>
                            <div className="scroll-prompt-badge">
                                <MdSwipe size={20} />
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>Swipe to view details</span>
                            </div>
                        </div>
                    )}

                    <table className="table mb-0 align-middle mobile-scroll-table">
                        <thead>
                            <tr>
                                <th className="small fw-bold text-muted sticky-col">BORROWER</th>
                                <th className="small fw-bold text-muted">AMOUNT</th>
                                <th className="small fw-bold text-muted">DATE</th>
                                <th className="small fw-bold text-muted text-end">STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.length > 0 ? (
                                filteredList.map((item, i) => (
                                    <tr key={item.id || i} className="loan-row">
                                        <td className="sticky-col">
                                            <div className="d-flex align-items-center">
                                                <div className="me-3 rounded-circle bg-light text-dark d-flex align-items-center justify-content-center fw-bold border" 
                                                     style={{ width: '36px', height: '36px', fontSize: '12px' }}>
                                                    {item.fullname?.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="fw-bold text-dark">{item.fullname}</span>
                                            </div>
                                        </td>
                                        <td className="fw-bold text-primary">
                                            {appContext.currency} {parseFloat(item.amount || 0).toLocaleString()}
                                        </td>
                                        <td>
                                            <div className="text-muted small d-flex align-items-center">
                                                <MdCalendarToday className="me-2 opacity-50" size={14} />
                                                {new Date(item.date).toLocaleDateString('en-GB', { 
                                                    day: 'numeric', month: 'short', year: 'numeric' 
                                                })}
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            {renderStatus(item.status)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-5">
                                        <h6 className="text-muted fw-light opacity-75">{STR_NO_RECORD[appContext.language]}</h6>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}